import { Controller } from "@hotwired/stimulus";

/**
 * Basic Neighbor Data Fields Controller
 *
 * Manages the dynamic field visibility and enabling/disabling for basic neighbor data workflow steps.
 * This controller is specifically designed for the 'basic_neighbor_data' step type where users
 * can select which neighbor fields to collect based on the selected data type (basic_info, contact_info, etc.).
 *
 * Features:
 * - Shows/hides field groups based on selected data type
 * - Enables/disables form inputs to prevent submission of irrelevant data
 * - Maintains state when switching between data types
 */
export default class extends Controller {
  static targets = ["dataTypeSelect", "fieldGroup", "requiredCheckbox"];
  static values = {
    currentDataType: String,
    optionalDataTypes: Array,
  };

  declare readonly dataTypeSelectTarget: HTMLSelectElement;
  declare readonly fieldGroupTargets: HTMLElement[];
  declare readonly hasRequiredCheckboxTarget: boolean;
  declare readonly requiredCheckboxTarget: HTMLElement;
  declare readonly currentDataTypeValue: string;
  declare readonly optionalDataTypesValue: string[];

  connect() {
    // Set the data type select to the current data type if it's not already set
    if (
      this.currentDataTypeValue &&
      this.dataTypeSelectTarget.value !== this.currentDataTypeValue
    ) {
      this.dataTypeSelectTarget.value = this.currentDataTypeValue;
    }
    this.updateFieldVisibility();
  }

  /**
   * Handles data type selection changes
   * Called when the user selects a different neighbor data type (basic_info, contact_info, etc.)
   */
  dataTypeChanged() {
    this.updateFieldVisibility();
  }

  /**
   * Updates field visibility and enabled state based on selected data type
   * Shows fields for the selected data type and hides/disables others
   */
  private updateFieldVisibility() {
    const selectedDataType = this.dataTypeSelectTarget.value;

    this.fieldGroupTargets.forEach((fieldGroup) => {
      const fieldDataType = fieldGroup.dataset.dataType;
      const isCurrentDataType = fieldDataType === this.currentDataTypeValue;
      const isSelectedDataType = fieldDataType === selectedDataType;

      if (isSelectedDataType) {
        // Show and enable fields for selected data type
        fieldGroup.classList.remove("hidden");
        fieldGroup.classList.remove("opacity-50");
        // this.enableFields(fieldGroup);
      } else {
        // Hide and disable fields for other data types
        fieldGroup.classList.add("hidden");
        // this.disableFields(fieldGroup);
      }
    });

    this.updateRequiredCheckboxVisibility(selectedDataType);
  }

  private updateRequiredCheckboxVisibility(selectedDataType: string) {
    if (!this.hasRequiredCheckboxTarget) return;

    const isOptional =
      this.optionalDataTypesValue.includes(selectedDataType);

    if (isOptional) {
      this.requiredCheckboxTarget.classList.remove("hidden");
    } else {
      this.requiredCheckboxTarget.classList.add("hidden");
      const checkbox = this.requiredCheckboxTarget.querySelector(
        'input[type="checkbox"]',
      ) as HTMLInputElement | null;
      if (checkbox) checkbox.checked = true;
    }
  }

  /**
   * Enables all form inputs within a container
   * Used when a field group becomes active for the selected data type
   */
  private enableFields(container: HTMLElement) {
    const inputs = container.querySelectorAll(
      'input[type="checkbox"], input[type="text"], textarea, select',
    );
    inputs.forEach((input) => {
      // Skip inputs that should remain disabled (those with data-keep-disabled='true')
      const keepDisabled = (input as HTMLElement).dataset.keepDisabled;
      if (keepDisabled !== "true") {
        (input as HTMLInputElement).disabled = false;
      }
    });
  }

  /**
   * Disables all form inputs within a container
   * Used when a field group becomes inactive to prevent submission of irrelevant data
   */
  private disableFields(container: HTMLElement) {
    const inputs = container.querySelectorAll(
      'input[type="checkbox"], input[type="text"], textarea, select',
    );
    inputs.forEach((input) => {
      // Always disable inputs in hidden field groups
      (input as HTMLInputElement).disabled = true;
    });
  }
}
